import { ComponentType, ReactNode, useMemo } from 'react';

import {
  ChartTooltipElementProps,
  Paper,
  ShadowDivider,
  Box,
} from '@cast/design-system';

import { CostReportChart } from 'features/cost-report/components/chart/CostReportChart';
import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { NormalizedCostChartData } from 'features/cost-report/types/costOverTime';
import { generateEmptyTimeSeries } from 'utils/data/generateEmptyTimeSeries';

import { useNormalizedCostChartConfig } from './useNormalizedCostChartConfig';

type Props = {
  header: ReactNode;
  data: NormalizedCostChartData[];
  loading?: boolean;
  tooltipHeader?: ComponentType<
    ChartTooltipElementProps<NormalizedCostChartData>
  >;
  tooltipFooter?: ComponentType<
    ChartTooltipElementProps<NormalizedCostChartData>
  >;
  chartOverlay?: ReactNode;
  testId?: string;
  /**
   * Build prompt for "Analyze with OpsPilot" feature.
   * If provided and chatbot is enabled, the button will appear automatically.
   */
  buildPrompt?: (range?: { from: number; to: number }) => string | null;
};

export const CostPerProvisionedResourceChart = ({
  header,
  data,
  loading,
  tooltipHeader,
  tooltipFooter,
  chartOverlay,
  testId,
  buildPrompt,
}: Props) => {
  const { chartType, currentDateRange, timezone } = useCostReportContext();
  const config = useNormalizedCostChartConfig();
  const emptyData = useMemo(
    () => generateEmptyTimeSeries({ range: currentDateRange }),
    [currentDateRange]
  );

  return (
    <Paper
      sx={{
        flex: 1,
        borderRadius: '4px',
        minWidth: 461,
        overflow: 'hidden',
      }}
      className="tour-highlight"
      testId={testId}
    >
      {header}
      <Box position="relative">
        <Box pb="10px" sx={{ position: 'relative' }}>
          <ShadowDivider opacity={0.2} />
        </Box>

        <>
          <CostReportChart
            timezone={timezone}
            chartType={chartType}
            isLoading={loading}
            tooltipHeader={tooltipHeader}
            tooltipFooter={tooltipFooter}
            hideSeriesInTooltip
            data={data?.length > 0 ? data : emptyData}
            height={150}
            chartSx={{ px: 8 }}
            config={config}
            buildPrompt={buildPrompt}
            yAxisProps={{
              ...(!data.length
                ? { ticks: [0, 1, 2, 3, 4], interval: 'preserveEnd' }
                : {}),
              tickCount: 3,
              tickFormatter: (value: string | number) => `$${value}`,
              tick: { dx: -8 },
              padding: { top: 12 },
            }}
          />
          {!!chartOverlay && (
            <Box position="absolute" sx={{ inset: 0 }}>
              {chartOverlay}
            </Box>
          )}
        </>
      </Box>
    </Paper>
  );
};
